/**
 * wizardly.ai - Smart Template Renderer (Gamma-style)
 *
 * Canvas-based rendering engine with SMART LAYOUT capabilities:
 * - Auto-sizing text based on content length
 * - Dynamic spacing based on item count
 * - Content-aware layout selection
 * - Overflow handling with graceful degradation
 * - Icon auto-selection based on content keywords
 * - Smart diagram rendering (funnel, pyramid, venn, etc.)
 * - Card-based layouts with visual hierarchy
 */

import { getTemplateForSlide, applyBrandColors } from '../data/themes';
import { parseTimelineItems } from './patternDetector';
import { renderIcon, selectIconForContent, selectIconsForBullets } from './iconRenderer';
import { renderFunnel, renderPyramid, renderVenn, renderQuadrant, renderBullseye, renderCycle, renderStaircase, renderChain, renderVersus, renderIceberg } from './smartDiagrams';

// Default slide dimensions (16:9 aspect ratio)
const DEFAULT_WIDTH = 1920;
const DEFAULT_HEIGHT = 1080;

// ============================================================================
// SMART LAYOUT ENGINE - Content Analysis
// ============================================================================

/**
 * Analyze content to determine optimal layout parameters
 */
function analyzeContent(content) {
  const analysis = {
    titleLength: (content.title || '').length,
    contentLength: (content.content || '').length,
    bulletCount: (content.bullet_points || content.bulletPoints || []).length,
    maxBulletLength: Math.max(
      0,
      ...(content.bullet_points || content.bulletPoints || []).map(b => b.length)
    ),
    totalBulletChars: (content.bullet_points || content.bulletPoints || [])
      .reduce((sum, b) => sum + b.length, 0),
    hasImage: !!content.image,

    // Derived metrics
    isTitleLong: false,
    isContentHeavy: false,
    needsCompactBullets: false,
  };

  // Determine flags
  analysis.isTitleLong = analysis.titleLength > 50;
  analysis.isContentHeavy = analysis.contentLength > 300 || analysis.totalBulletChars > 400;
  analysis.needsCompactBullets = analysis.bulletCount > 5 || analysis.maxBulletLength > 80;

  return analysis;
}

/**
 * Calculate optimal font size based on text length and available space
 */
function calculateSmartFontSize(text, baseFontSize, maxWidth, ctx, slideHeight) {
  const basePixelSize = (baseFontSize / 100) * slideHeight;

  // Set font to measure
  ctx.font = `700 ${basePixelSize}px Inter, system-ui, sans-serif`;
  const textWidth = ctx.measureText(text).width;

  // If text fits, use base size
  if (textWidth <= maxWidth) {
    return baseFontSize;
  }

  // Calculate scale factor needed
  const scaleFactor = maxWidth / textWidth;
  const scaledSize = baseFontSize * scaleFactor;

  // Don't go below 60% of base size
  const minSize = baseFontSize * 0.6;
  return Math.max(scaledSize, minSize);
}

/**
 * Calculate optimal line height and spacing for bullets based on count
 */
function calculateSmartBulletSpacing(bulletCount, availableHeight, baseFontSize, slideHeight) {
  const basePixelSize = (baseFontSize / 100) * slideHeight;
  const minLineHeight = 1.4;
  const maxLineHeight = 2.5;

  // Calculate space needed per bullet at max line height
  const spacePerBulletMax = basePixelSize * maxLineHeight;
  const totalSpaceNeeded = bulletCount * spacePerBulletMax;

  if (totalSpaceNeeded <= availableHeight) {
    // Plenty of space, use comfortable spacing
    return { lineHeight: maxLineHeight, fontSize: baseFontSize };
  }

  // Need to compress - first try reducing line height
  const compressedLineHeight = Math.max(
    minLineHeight,
    availableHeight / (bulletCount * basePixelSize)
  );

  if (compressedLineHeight >= minLineHeight) {
    return { lineHeight: compressedLineHeight, fontSize: baseFontSize };
  }

  // Still not enough, reduce font size too
  const scaleFactor = availableHeight / (bulletCount * basePixelSize * minLineHeight);
  const newFontSize = baseFontSize * Math.max(0.7, scaleFactor);

  return { lineHeight: minLineHeight, fontSize: newFontSize };
}

// ============================================================================
// MAIN RENDER FUNCTION
// ============================================================================

/**
 * Render a single slide using a template with SMART layout adaptation
 */
export async function renderTemplateToImage(template, content, colorScheme, options = {}) {
  const width = options.width || DEFAULT_WIDTH;
  const height = options.height || DEFAULT_HEIGHT;

  // Create offscreen canvas
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  // Enable font smoothing
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Analyze content for smart adaptation
  const analysis = analyzeContent(content);

  // Render background
  await renderBackground(ctx, template.layout.background, colorScheme, width, height);

  // Render decorative shapes for visually sparse templates
  renderDecorations(ctx, template.id, colorScheme, width, height);

  // Render each element with smart adaptation
  for (const element of template.layout.elements) {
    await renderSmartElement(ctx, element, content, colorScheme, width, height, analysis);
  }

  return canvas.toDataURL('image/png');
}

/**
 * Render only background + decorative shapes (no text) for streaming animation
 */
export async function renderTemplateBackgroundOnly(template, content, colorScheme, options = {}) {
  const width = options.width || DEFAULT_WIDTH;
  const height = options.height || DEFAULT_HEIGHT;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  const analysis = analyzeContent(content);

  await renderBackground(ctx, template.layout.background, colorScheme, width, height);

  // Render decorative shapes
  renderDecorations(ctx, template.id, colorScheme, width, height);

  // Only render shape elements (accent bars, rectangles) — skip all text-bearing elements
  for (const element of template.layout.elements) {
    if (element.type === 'shape') {
      await renderSmartElement(ctx, element, content, colorScheme, width, height, analysis);
    }
  }

  return canvas.toDataURL('image/png');
}

/**
 * Render background (solid, gradient, or image)
 */
async function renderBackground(ctx, background, colorScheme, width, height) {
  if (background.type === 'gradient') {
    const gradient = parseGradient(ctx, background.value, width, height);
    ctx.fillStyle = gradient;
  } else if (background.type === 'image') {
    try {
      const img = await loadImage(background.value);
      ctx.drawImage(img, 0, 0, width, height);
      if (background.overlay) {
        ctx.fillStyle = background.overlay;
        ctx.fillRect(0, 0, width, height);
      }
      return;
    } catch (e) {
      console.warn('Failed to load background image, using fallback color');
      ctx.fillStyle = colorScheme.background;
    }
  } else {
    ctx.fillStyle = background.value || colorScheme.background;
  }
  ctx.fillRect(0, 0, width, height);
}

/**
 * Render element with SMART adaptation based on content analysis
 */
async function renderSmartElement(ctx, element, content, colorScheme, width, height, analysis) {
  const x = (element.position.x / 100) * width;
  const y = (element.position.y / 100) * height;
  const w = (element.position.width / 100) * width;
  const h = (element.position.height / 100) * height;

  const style = { ...element.style };

  switch (element.type) {
    case 'shape':
      renderShape(ctx, x, y, w, h, style, colorScheme);
      break;

    case 'title':
      const titleText = content[element.placeholder] || '';
      if (titleText) {
        // SMART: Auto-size title if too long
        if (analysis.isTitleLong) {
          style.fontSize = calculateSmartFontSize(titleText, style.fontSize, w, ctx, height);
        }
        renderText(ctx, titleText, x, y, w, h, style, colorScheme, height);
      }
      break;

    case 'subtitle':
    case 'content':
      const text = content[element.placeholder] || '';
      if (text) {
        // SMART: Reduce font size for heavy content
        if (analysis.isContentHeavy && element.type === 'content') {
          style.fontSize = style.fontSize * 0.85;
        }
        renderText(ctx, text, x, y, w, h, style, colorScheme, height);
      }
      break;

    case 'bullets':
      const bullets = content[element.placeholder] || content.bulletPoints || content.bullet_points || [];
      if (bullets.length > 0) {
        // SMART: Adjust spacing and font size based on bullet count
        const smartSpacing = calculateSmartBulletSpacing(
          bullets.length,
          h,
          style.fontSize,
          height
        );
        style.fontSize = smartSpacing.fontSize;
        style.lineHeight = smartSpacing.lineHeight;

        renderBullets(ctx, bullets, x, y, w, h, style, colorScheme, height);
      }
      break;

    case 'bigNumber':
      const number = content[element.placeholder] || content.number || '';
      if (number) {
        renderBigNumber(ctx, number, x, y, w, h, style, colorScheme, height);
      }
      break;

    case 'quote':
      const quote = content[element.placeholder] || content.quote || '';
      if (quote) {
        renderQuote(ctx, quote, x, y, w, h, style, colorScheme, height, content.attribution);
      }
      break;

    case 'image':
      if (content.image) {
        await renderImage(ctx, content.image, x, y, w, h, style);
      }
      break;

    case 'comparison':
      if (content.leftItems && content.rightItems) {
        renderComparison(ctx, content, x, y, w, h, style, colorScheme, height);
      }
      break;

    case 'timeline':
      const timelineBullets = content.bullet_points || content.bulletPoints || [];
      if (timelineBullets.length > 0) {
        const timelineItems = parseTimelineItems(timelineBullets);
        renderTimeline(ctx, timelineItems, x, y, w, h, style, colorScheme, height);
      }
      break;

    case 'process':
      const processBullets = content.bullet_points || content.bulletPoints || [];
      if (processBullets.length > 0) {
        renderProcess(ctx, processBullets, x, y, w, h, style, colorScheme, height);
      }
      break;

    case 'stat':
      const statValue = content.stat || extractFirstStat(content) || '';
      if (statValue) {
        renderStat(ctx, statValue, x, y, w, h, style, colorScheme, height);
      }
      break;

    // ============ SMART LAYOUTS (Gamma-style) ============

    case 'icon-bullets':
      // Bullet points with auto-selected icons
      const iconBullets = content[element.placeholder] || content.bulletPoints || content.bullet_points || [];
      if (iconBullets.length > 0) {
        renderIconBullets(ctx, iconBullets, x, y, w, h, style, colorScheme, height);
      }
      break;

    case 'icon-grid':
      // Grid of icons with labels (like Gamma's icon layout)
      const gridBullets = content[element.placeholder] || content.bulletPoints || content.bullet_points || [];
      if (gridBullets.length > 0) {
        renderIconGrid(ctx, gridBullets, x, y, w, h, style, colorScheme, height);
      }
      break;

    case 'cards':
      // Card-based layout with content in styled boxes
      const cardBullets = content[element.placeholder] || content.bulletPoints || content.bullet_points || [];
      if (cardBullets.length > 0) {
        renderCards(ctx, cardBullets, x, y, w, h, style, colorScheme, height);
      }
      break;

    case 'feature-showcase':
      const featureItems = content[element.placeholder] || content.bulletPoints || content.bullet_points || [];
      if (featureItems.length > 0) {
        renderFeatureShowcase(ctx, featureItems, x, y, w, h, style, colorScheme, height);
      }
      break;

    case 'numbered-list':
      const numberedItems = content[element.placeholder] || content.bulletPoints || content.bullet_points || [];
      if (numberedItems.length > 0) {
        renderNumberedList(ctx, numberedItems, x, y, w, h, style, colorScheme, height);
      }
      break;

    case 'big-statement':
      const statementText = content[element.placeholder] || content.title || '';
      if (statementText) {
        renderBigStatement(ctx, statementText, x, y, w, h, style, colorScheme, height);
      }
      break;

    case 'metrics-dashboard':
      const metricsItems = content[element.placeholder] || content.bulletPoints || content.bullet_points || [];
      if (metricsItems.length > 0) {
        renderMetricsDashboard(ctx, metricsItems, x, y, w, h, style, colorScheme, height);
      }
      break;

    case 'funnel':
      const funnelItems = content.bulletPoints || content.bullet_points || [];
      if (funnelItems.length > 0) {
        renderFunnel(ctx, funnelItems, x, y, w, h, colorScheme);
      }
      break;

    case 'pyramid':
      const pyramidItems = content.bulletPoints || content.bullet_points || [];
      if (pyramidItems.length > 0) {
        renderPyramid(ctx, pyramidItems, x, y, w, h, colorScheme);
      }
      break;

    case 'venn':
      const vennItems = content.bulletPoints || content.bullet_points || [];
      if (vennItems.length >= 2) {
        renderVenn(ctx, vennItems, x, y, w, h, colorScheme);
      }
      break;

    case 'quadrant':
      const quadrantItems = content.bulletPoints || content.bullet_points || [];
      if (quadrantItems.length > 0) {
        renderQuadrant(ctx, quadrantItems, x, y, w, h, colorScheme);
      }
      break;

    case 'bullseye':
      const bullseyeItems = content.bulletPoints || content.bullet_points || [];
      if (bullseyeItems.length > 0) {
        renderBullseye(ctx, bullseyeItems, x, y, w, h, colorScheme);
      }
      break;

    case 'cycle':
      const cycleItems = content.bulletPoints || content.bullet_points || [];
      if (cycleItems.length >= 2) {
        renderCycle(ctx, cycleItems, x, y, w, h, colorScheme);
      }
      break;

    case 'staircase':
      const staircaseItems = content.bulletPoints || content.bullet_points || [];
      if (staircaseItems.length > 0) {
        renderStaircase(ctx, staircaseItems, x, y, w, h, colorScheme);
      }
      break;

    case 'chain':
      const chainItems = content.bulletPoints || content.bullet_points || [];
      if (chainItems.length > 0) {
        renderChain(ctx, chainItems, x, y, w, h, colorScheme);
      }
      break;

    case 'versus':
      const vsBullets = content.bulletPoints || content.bullet_points || [];
      if (vsBullets.length >= 2) {
        renderVersus(ctx, vsBullets, x, y, w, h, colorScheme);
      }
      break;

    case 'iceberg':
      const icebergItems = content.bulletPoints || content.bullet_points || [];
      if (icebergItems.length >= 2) {
        renderIceberg(ctx, icebergItems, x, y, w, h, colorScheme);
      }
      break;

    case 'image-placeholder':
      if (content._slideImageUrl) {
        await renderActualImage(ctx, content._slideImageUrl, x, y, w, h, style);
      } else if (content._needsImageLoading) {
        renderSkeletonPlaceholder(ctx, x, y, w, h, style, colorScheme, height);
      } else {
        renderImagePlaceholder(ctx, content.title || '', x, y, w, h, style, colorScheme, height);
      }
      break;

    case 'three-column':
      const colBullets = content[element.placeholder] || content.bulletPoints || content.bullet_points || [];
      if (colBullets.length > 0) {
        renderThreeColumns(ctx, colBullets, x, y, w, h, style, colorScheme, height);
      }
      break;

    case 'quote-card':
      const quoteText = content[element.placeholder] || content.title || '';
      const attribution = content.content || content.attribution || '';
      renderQuoteCard(ctx, quoteText, attribution, x, y, w, h, style, colorScheme, height);
      break;

    case 'tag-label':
      const tagText = getTagLabelText(content);
      if (tagText) {
        renderTagLabel(ctx, tagText, x, y, w, h, style, colorScheme, height);
      }
      break;

    default:
      break;
  }
}

// ============================================================================
// TAG LABEL RENDERER (Gamma-style uppercase tag above title)
// ============================================================================

const TAG_LABEL_MAP = {
  'content': 'OVERVIEW', 'bullets': 'KEY POINTS', 'icon-bullets': 'HIGHLIGHTS',
  'icon-grid': 'AT A GLANCE', 'cards': 'DETAILS', 'process': 'OUR PROCESS',
  'timeline': 'TIMELINE', 'feature-showcase': 'FEATURES', 'stat-highlight': 'BY THE NUMBERS',
  'three-column': 'BREAKDOWN', 'comparison': 'COMPARISON', 'numbered-list': 'STEPS',
  'quote': 'QUOTE', 'quote-card': 'QUOTE', 'metrics-dashboard': 'METRICS',
  'funnel': 'FUNNEL', 'pyramid': 'HIERARCHY', 'venn': 'OVERLAP',
  'cycle': 'CYCLE', 'staircase': 'PROGRESSION', 'chain': 'CHAIN',
  'versus': 'VERSUS', 'iceberg': 'DEEPER LOOK', 'big-statement': 'KEY INSIGHT',
  'text-with-image': 'OVERVIEW',
};

function getTagLabelText(content) {
  const slideType = content.slideType || content.slide_type || '';
  return TAG_LABEL_MAP[slideType] || '';
}

function renderTagLabel(ctx, text, x, y, w, h, style, colorScheme, slideHeight) {
  const ws = style._widthScale || 1;
  const fontSize = ((style.fontSize || 1.3) / 100) * slideHeight * ws;
  const fontFamily = style.fontFamily || 'Inter, system-ui, sans-serif';

  ctx.font = `${style.fontWeight || '600'} ${fontSize}px ${fontFamily}`;
  ctx.fillStyle = style.color || colorScheme.primary;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';

  // Small accent dot before label
  const dotRadius = fontSize * 0.3;
  ctx.beginPath();
  ctx.arc(x + dotRadius, y + h / 2, dotRadius, 0, Math.PI * 2);
  ctx.fill();

  // Uppercase text with letter spacing simulation
  const labelText = text.toUpperCase();
  const spacedText = labelText.split('').join(String.fromCharCode(8202)); // Hair space for letter spacing
  ctx.fillText(spacedText, x + dotRadius * 3 + fontSize * 0.4, y + h / 2);
}

// ============================================================================
// SMART LAYOUT RENDERERS (Gamma-style)
// ============================================================================

/**
 * Render bullets with auto-selected icons (instead of dots)
 */
function renderIconBullets(ctx, bullets, x, y, maxWidth, maxHeight, style, colorScheme, slideHeight) {
  const ws = style._widthScale || 1;
  // Adaptive font sizing: scale up when few items, scale down when many
  const baseFontSize = (style.fontSize / 100) * slideHeight * ws;
  const scaleFactor = bullets.length <= 3 ? 1.35 : bullets.length <= 5 ? 1.15 : 1.0;
  const fontSize = baseFontSize * scaleFactor;
  const fontFamily = style.fontFamily || 'Inter, system-ui, sans-serif';
  const fontStyle = style.fontStyle === 'italic' ? 'italic ' : '';
  const textColor = style.color || colorScheme.text;
  const iconSize = fontSize * 1.1;
  const icons = selectIconsForBullets(bullets);

  const cardPad = fontSize * 0.45;
  const cardRadius = slideHeight * 0.01;
  const iconMarginRight = slideHeight * 0.015;
  const textX = x + iconSize + iconMarginRight;
  const textMaxWidth = maxWidth - iconSize - iconMarginRight;
  const gap = slideHeight * 0.012;

  // Pre-measure all items to calculate row heights
  ctx.font = `${fontStyle}500 ${fontSize}px ${fontFamily.split(',')[0]}`;
  const itemMeasures = bullets.map(bullet => {
    const words = bullet.split(' ');
    let lines = [];
    let currentLine = '';
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(testLine).width > textMaxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) lines.push(currentLine);
    const textBlockHeight = lines.length * fontSize * 1.3;
    const rowHeight = Math.max(iconSize, textBlockHeight);
    return { lines, rowHeight };
  });

  // Group items tightly with fixed gap, then vertically center the group
  const totalContentH = itemMeasures.reduce((sum, m) => sum + m.rowHeight + cardPad * 2, 0) + gap * (bullets.length - 1);
  const startOffset = Math.max(0, (maxHeight - totalContentH) / 2);

  let currentY = y + startOffset;

  bullets.forEach((bullet, i) => {
    if (currentY + fontSize > y + maxHeight) return;
    const { lines, rowHeight } = itemMeasures[i];

    // Background card
    ctx.fillStyle = hexToRgba(colorScheme.primary, 0.04);
    roundRect(ctx, x - cardPad, currentY - cardPad, maxWidth + cardPad * 2, rowHeight + cardPad * 2, cardRadius);
    ctx.fill();

    // Icon
    renderIcon(ctx, icons[i] || 'check-circle', x, currentY - 2, iconSize, colorScheme.primary, {
      backgroundColor: hexToRgba(colorScheme.primary, 0.15),
      backgroundRadius: iconSize * 0.7,
    });

    // Text
    ctx.fillStyle = textColor;
    ctx.font = `${fontStyle}500 ${fontSize}px ${fontFamily.split(',')[0]}`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    let lineY = currentY;
    lines.forEach(line => {
      ctx.fillText(line, textX, lineY);
      lineY += fontSize * 1.3;
    });

    currentY += rowHeight + cardPad * 2 + gap;
  });
}

/**
 * Render icon grid (icons with labels in a grid layout)
 */
function renderIconGrid(ctx, items, x, y, maxWidth, maxHeight, style, colorScheme, slideHeight) {
  const count = items.length;
  const cols = count <= 3 ? count : count === 4 ? 2 : count <= 6 ? 3 : 4;
  const rows = Math.ceil(count / cols);

  const gap = slideHeight * 0.015;
  const cellWidth = (maxWidth - gap * (cols - 1)) / cols;
  const cellHeight = (maxHeight - gap * (rows - 1)) / rows;

  const ws = style._widthScale || 1;
  const iconSize = Math.min(slideHeight * 0.055 * ws, cellHeight * 0.20 * ws);
  const titleFontSize = Math.max(slideHeight * 0.028 * ws, Math.min(slideHeight * 0.034 * ws, cellHeight * 0.10 * ws));
  const descFontSize = titleFontSize * 0.78;
  const accentLineH = slideHeight * 0.003;

  const icons = selectIconsForBullets(items);
  const gridAccentColors = [colorScheme.primary, colorScheme.secondary, colorScheme.accent || colorScheme.primary];

  items.forEach((item, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cellX = x + col * (cellWidth + gap);
    const cellY = y + row * (cellHeight + gap);
    const centerX = cellX + cellWidth / 2;
    const accentColor = gridAccentColors[i % gridAccentColors.length];

    // Subtle card background
    ctx.fillStyle = hexToRgba(accentColor, 0.05);
    roundRect(ctx, cellX, cellY, cellWidth, cellHeight, slideHeight * 0.01);
    ctx.fill();

    // Split into title:description
    let titleText = item;
    let descText = '';
    const colonIdx = item.indexOf(':');
    const dotIdx = item.indexOf('. ');
    if (colonIdx > 0 && colonIdx < 60) {
      titleText = item.substring(0, colonIdx).trim();
      descText = item.substring(colonIdx + 1).trim();
    } else if (dotIdx > 0 && dotIdx < 60) {
      titleText = item.substring(0, dotIdx).trim();
      descText = item.substring(dotIdx + 2).trim();
    }

    // Pre-measure title lines
    const maxLineWidth = cellWidth - slideHeight * 0.03;
    ctx.font = `600 ${titleFontSize}px Inter, system-ui, sans-serif`;
    let titleLines = [];
    let tl = '';
    for (const word of titleText.split(' ')) {
      const test = tl ? `${tl} ${word}` : word;
      if (ctx.measureText(test).width > maxLineWidth && tl) { titleLines.push(tl); tl = word; }
      else tl = test;
    }
    if (tl) titleLines.push(tl);

    let descLines = [];
    if (descText) {
      ctx.font = `400 ${descFontSize}px Inter, system-ui, sans-serif`;
      let dl = '';
      for (const word of descText.split(' ')) {
        const test = dl ? `${dl} ${word}` : word;
        if (ctx.measureText(test).width > maxLineWidth && dl) { descLines.push(dl); dl = word; }
        else dl = test;
      }
      if (dl) descLines.push(dl);
    }

    // Calculate content height and vertically center
    const gapAfterIcon = slideHeight * 0.012;
    const gapAfterAccent = slideHeight * 0.012;
    const contentH = iconSize + gapAfterIcon + accentLineH + gapAfterAccent
      + titleLines.length * titleFontSize * 1.3
      + (descLines.length > 0 ? titleFontSize * 0.2 + descLines.length * descFontSize * 1.4 : 0);
    const topOffset = Math.max(0, (cellHeight - contentH) / 2);

    // Icon
    const iconY = cellY + topOffset;
    ctx.shadowColor = 'rgba(0,0,0,0.08)';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 2;
    ctx.fillStyle = hexToRgba(accentColor, 0.12);
    ctx.beginPath();
    ctx.arc(centerX, iconY + iconSize / 2, iconSize * 0.75, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;
    renderIcon(ctx, icons[i] || 'star', centerX - iconSize / 2, iconY, iconSize, accentColor);

    // Accent divider
    const accentLineY = iconY + iconSize + gapAfterIcon;
    const accentLineWidth = Math.min(cellWidth * 0.5, slideHeight * 0.12);
    ctx.fillStyle = accentColor;
    ctx.fillRect(centerX - accentLineWidth / 2, accentLineY, accentLineWidth, accentLineH);

    // Title
    ctx.fillStyle = colorScheme.text;
    ctx.font = `600 ${titleFontSize}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    let textY = accentLineY + accentLineH + gapAfterAccent;
    titleLines.slice(0, 2).forEach(line => {
      ctx.fillText(line, centerX, textY);
      textY += titleFontSize * 1.3;
    });

    // Description
    if (descLines.length > 0) {
      textY += titleFontSize * 0.2;
      ctx.fillStyle = colorScheme.textMuted;
      ctx.font = `400 ${descFontSize}px Inter, system-ui, sans-serif`;
      const descLineH = descFontSize * 1.4;
      const maxDescY = cellY + cellHeight - slideHeight * 0.01;
      descLines.forEach(line => {
        if (textY + descFontSize > maxDescY) return;
        ctx.fillText(line, centerX, textY);
        textY += descLineH;
      });
    }
  });
}

/**
 * Render card-based layout with content in styled boxes
 */
function renderCards(ctx, items, x, y, maxWidth, maxHeight, style, colorScheme, slideHeight) {
  const count = items.length;
  const cols = count <= 2 ? count : count <= 4 ? 2 : 3;
  const rows = Math.ceil(count / cols);

  const gap = slideHeight * 0.018;
  const cardWidth = (maxWidth - gap * (cols - 1)) / cols;
  const cardHeight = (maxHeight - gap * (rows - 1)) / rows;
  const cardRadius = slideHeight * 0.012;
  const cardPadding = slideHeight * 0.018;

  const icons = selectIconsForBullets(items);
  const accentColors = [colorScheme.primary, colorScheme.secondary, colorScheme.accent];
  const cardBgColors = [
    hexToRgba(colorScheme.primary, 0.10),
    hexToRgba(colorScheme.secondary, 0.10),
    hexToRgba(colorScheme.accent, 0.10),
    hexToRgba(colorScheme.primary, 0.08),
    hexToRgba(colorScheme.secondary, 0.08),
    hexToRgba(colorScheme.accent, 0.08),
  ];

  const ws = style._widthScale || 1;
  const iconSize = slideHeight * 0.045 * ws;
  const accentHeight = slideHeight * 0.005;
  const titleFontSize = Math.max(slideHeight * 0.030 * ws, Math.min(slideHeight * 0.036 * ws, cardHeight * 0.08 * ws));
  const descFontSize = titleFontSize * 0.78;

  items.forEach((item, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cardX = x + col * (cardWidth + gap);
    const cardY = y + row * (cardHeight + gap);
    const accentColor = accentColors[i % 3];

    // Card shadow
    ctx.shadowColor = 'rgba(0,0,0,0.06)';
    ctx.shadowBlur = 10;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 3;
    ctx.fillStyle = cardBgColors[i % cardBgColors.length];
    roundRect(ctx, cardX, cardY, cardWidth, cardHeight, cardRadius);
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    // Top accent bar
    ctx.fillStyle = accentColor;
    roundRect(ctx, cardX, cardY, cardWidth, accentHeight, cardRadius);
    ctx.fill();

    // Split into title:description
    let titleText = item;
    let descText = '';
    const colonIdx = item.indexOf(':');
    const dotIdx = item.indexOf('. ');
    if (colonIdx > 0 && colonIdx < 60) {
      titleText = item.substring(0, colonIdx).trim();
      descText = item.substring(colonIdx + 1).trim();
    } else if (dotIdx > 0 && dotIdx < 60) {
      titleText = item.substring(0, dotIdx).trim();
      descText = item.substring(dotIdx + 2).trim();
    }

    // Calculate content height to vertically center within card
    const maxTextWidth = cardWidth - cardPadding * 2;
    ctx.font = `700 ${titleFontSize}px Inter, system-ui, sans-serif`;
    let titleLines = [];
    let tl = '';
    for (const word of titleText.split(' ')) {
      const test = tl ? `${tl} ${word}` : word;
      if (ctx.measureText(test).width > maxTextWidth && tl) { titleLines.push(tl); tl = word; }
      else tl = test;
    }
    if (tl) titleLines.push(tl);

    let descLines = [];
    if (descText) {
      ctx.font = `400 ${descFontSize}px Inter, system-ui, sans-serif`;
      let dl = '';
      for (const word of descText.split(' ')) {
        const test = dl ? `${dl} ${word}` : word;
        if (ctx.measureText(test).width > maxTextWidth && dl) { descLines.push(dl); dl = word; }
        else dl = test;
      }
      if (dl) descLines.push(dl);
    }

    const contentH = iconSize + cardPadding * 0.4 + titleLines.length * titleFontSize * 1.3
      + (descLines.length > 0 ? titleFontSize * 0.3 + descLines.length * descFontSize * 1.4 : 0);
    const availableInner = cardHeight - accentHeight - cardPadding * 2;
    const topOffset = Math.max(0, (availableInner - contentH) / 2);

    // Icon
    const iconStartY = cardY + accentHeight + cardPadding + topOffset;
    const iconCenterX = cardX + cardPadding + iconSize * 0.5;
    const iconCenterY = iconStartY + iconSize * 0.5;
    ctx.fillStyle = hexToRgba(accentColor, 0.12);
    ctx.beginPath();
    ctx.arc(iconCenterX, iconCenterY, iconSize * 0.6, 0, Math.PI * 2);
    ctx.fill();
    renderIcon(ctx, icons[i] || 'star', iconCenterX - iconSize * 0.4, iconCenterY - iconSize * 0.4, iconSize * 0.8, accentColor);

    // Title
    let textY = iconStartY + iconSize + cardPadding * 0.4;
    ctx.fillStyle = colorScheme.text;
    ctx.font = `700 ${titleFontSize}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    titleLines.forEach(line => {
      ctx.fillText(line, cardX + cardPadding, textY);
      textY += titleFontSize * 1.3;
    });

    // Description
    if (descLines.length > 0) {
      textY += titleFontSize * 0.3;
      ctx.fillStyle = colorScheme.textMuted;
      ctx.font = `400 ${descFontSize}px Inter, system-ui, sans-serif`;
      const maxDescY = cardY + cardHeight - cardPadding;
      descLines.forEach(line => {
        if (textY + descFontSize > maxDescY) return;
        ctx.fillText(line, cardX + cardPadding, textY);
        textY += descFontSize * 1.4;
      });
    }
  });
}

/**
 * Render feature showcase (grid of feature cards with icon + title + description)
 */
function renderFeatureShowcase(ctx, items, x, y, maxWidth, maxHeight, style, colorScheme, slideHeight) {
  const count = items.length;
  const cols = count <= 2 ? 2 : count <= 4 ? 2 : 3;
  const rows = Math.ceil(count / cols);
  const gap = slideHeight * 0.018;

  const cardWidth = (maxWidth - gap * (cols - 1)) / cols;
  const cardHeight = (maxHeight - gap * (rows - 1)) / rows;
  const cardRadius = slideHeight * 0.012;
  const cardPadding = slideHeight * 0.022;

  const ws = style._widthScale || 1;
  const icons = selectIconsForBullets(items);
  const iconSize = slideHeight * 0.045 * ws;
  const baseFontSize = style.fontSize ? (style.fontSize / 100) * slideHeight : slideHeight * 0.030 * ws;
  const titleFontSize = Math.max(baseFontSize, slideHeight * 0.034 * ws);
  const descFontSize = titleFontSize * 0.78;

  items.forEach((item, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cardX = x + col * (cardWidth + gap);
    const cardY = y + row * (cardHeight + gap);

    // Card shadow
    ctx.shadowColor = 'rgba(0,0,0,0.08)';
    ctx.shadowBlur = 12;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 4;

    // Card background with primary tint
    const featureAccentColors = [colorScheme.primary, colorScheme.secondary, colorScheme.accent || colorScheme.primary];
    ctx.fillStyle = hexToRgba(colorScheme.primary, 0.10);
    roundRect(ctx, cardX, cardY, cardWidth, cardHeight, cardRadius);
    ctx.fill();

    // Reset shadow
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    // Colored accent divider line at top of card (Gamma-style)
    const accentHeight = slideHeight * 0.005;
    const featureAccentColor = featureAccentColors[i % featureAccentColors.length];
    ctx.fillStyle = featureAccentColor;
    roundRect(ctx, cardX, cardY, cardWidth, accentHeight, cardRadius);
    ctx.fill();

    // Split bullet into title:description
    let titleText = item;
    let descText = '';
    const colonIdx = item.indexOf(':');
    const dotIdx = item.indexOf('. ');
    if (colonIdx > 0 && colonIdx < 60) {
      titleText = item.substring(0, colonIdx).trim();
      descText = item.substring(colonIdx + 1).trim();
    } else if (dotIdx > 0 && dotIdx < 60) {
      titleText = item.substring(0, dotIdx).trim();
      descText = item.substring(dotIdx + 2).trim();
    }

    const maxTextWidth = cardWidth - cardPadding * 2;

    // Pre-measure title lines
    ctx.font = `700 ${titleFontSize}px Inter, system-ui, sans-serif`;
    let truncTitle = titleText;
    if (ctx.measureText(titleText).width > maxTextWidth) {
      while (ctx.measureText(truncTitle + '...').width > maxTextWidth && truncTitle.length > 0) {
        truncTitle = truncTitle.slice(0, -1);
      }
      truncTitle += '...';
    }
    const titleLineCount = 1;

    // Pre-measure description lines
    let descLines = [];
    if (descText) {
      ctx.font = `400 ${descFontSize}px Inter, system-ui, sans-serif`;
      const words = descText.split(' ');
      let currentLine = '';
      for (const word of words) {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        if (ctx.measureText(testLine).width > maxTextWidth && currentLine) {
          descLines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }
      if (currentLine) descLines.push(currentLine);
    }

    // Calculate total content height and vertical centering offset
    const gapAfterIcon = cardPadding * 0.4;
    const contentH = iconSize + gapAfterIcon
      + titleLineCount * titleFontSize * 1.3
      + (descLines.length > 0 ? titleFontSize * 0.3 + descLines.length * descFontSize * 1.4 : 0);
    const availableInner = cardHeight - accentHeight - cardPadding * 2;
    const topOffset = Math.max(0, (availableInner - contentH) / 2);

    // Icon centered at top of content block
    const iconStartY = cardY + accentHeight + cardPadding + topOffset;
    const iconX = cardX + (cardWidth - iconSize) / 2;
    renderIcon(ctx, icons[i] || 'star', iconX, iconStartY, iconSize, featureAccentColor, {
      backgroundColor: hexToRgba(featureAccentColor, 0.15),
      backgroundRadius: iconSize * 0.6,
    });

    const textStartY = iconStartY + iconSize + gapAfterIcon;

    // Title (bold, centered)
    ctx.fillStyle = colorScheme.text;
    ctx.font = `700 ${titleFontSize}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(truncTitle, cardX + cardWidth / 2, textStartY);

    // Description (muted, centered, word-wrapped)
    if (descLines.length > 0) {
      ctx.fillStyle = colorScheme.textMuted;
      ctx.font = `400 ${descFontSize}px Inter, system-ui, sans-serif`;
      const descStartY = textStartY + titleFontSize * 1.6;
      const maxLines = Math.floor((cardY + cardHeight - descStartY - cardPadding) / (descFontSize * 1.4));
      descLines.slice(0, Math.max(maxLines, 1)).forEach((line, li) => {
        ctx.fillText(line, cardX + cardWidth / 2, descStartY + li * descFontSize * 1.4);
      });
    }
  });
}

/**
 * Render numbered list (prominent numbers with text)
 */
function renderNumberedList(ctx, items, x, y, maxWidth, maxHeight, style, colorScheme, slideHeight) {
  const count = Math.min(items.length, 8);
  const ws = style._widthScale || 1;
  const baseFontSize = style.fontSize ? (style.fontSize / 100) * slideHeight : slideHeight * 0.030 * ws;
  const titleFontSize = Math.max(baseFontSize, slideHeight * 0.030 * ws);
  const descFontSize = titleFontSize * 0.78;
  const numberSize = titleFontSize * 1.3;
  const circleRadius = numberSize * 0.6;
  // Distribute items evenly across the full height
  const itemSpacing = maxHeight / count;

  items.slice(0, count).forEach((item, i) => {
    const itemY = y + i * itemSpacing;
    if (itemY + circleRadius * 2 > y + maxHeight) return;

    const cleanText = item.replace(/^\s*\d+[\.\)\-:\s]+/, '').trim();

    // Split into title:description
    let titleText = cleanText;
    let descText = '';
    const colonIdx = cleanText.indexOf(':');
    const dotIdx = cleanText.indexOf('. ');
    if (colonIdx > 0 && colonIdx < 60) {
      titleText = cleanText.substring(0, colonIdx).trim();
      descText = cleanText.substring(colonIdx + 1).trim();
    } else if (dotIdx > 0 && dotIdx < 60) {
      titleText = cleanText.substring(0, dotIdx).trim();
      descText = cleanText.substring(dotIdx + 2).trim();
    }

    // Subtle row background
    ctx.fillStyle = hexToRgba(colorScheme.primary, i % 2 === 0 ? 0.03 : 0.0);
    roundRect(ctx, x, itemY, maxWidth, itemSpacing - slideHeight * 0.005, slideHeight * 0.006);
    ctx.fill();

    // Number circle
    const circleX = x + circleRadius + slideHeight * 0.005;
    const circleCenterY = itemY + itemSpacing / 2;
    ctx.beginPath();
    ctx.arc(circleX, circleCenterY, circleRadius, 0, Math.PI * 2);
    ctx.fillStyle = colorScheme.primary;
    ctx.fill();

    // Number text
    ctx.fillStyle = colorScheme.background || '#ffffff';
    ctx.font = `700 ${numberSize * 0.6}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${i + 1}`, circleX, circleCenterY);

    // Text area
    const textX = x + circleRadius * 2 + slideHeight * 0.02;
    const textMaxWidth = maxWidth - circleRadius * 2 - slideHeight * 0.025;

    // Title (bold)
    ctx.fillStyle = colorScheme.text;
    ctx.font = `600 ${titleFontSize}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    let textY = itemY + slideHeight * 0.008;
    // If no description, vertically center the title
    if (!descText) {
      textY = circleCenterY - titleFontSize / 2;
    }

    // Word wrap title
    const titleWords = titleText.split(' ');
    let tLine = '';
    for (const word of titleWords) {
      const test = tLine ? `${tLine} ${word}` : word;
      if (ctx.measureText(test).width > textMaxWidth && tLine) {
        ctx.fillText(tLine, textX, textY);
        textY += titleFontSize * 1.3;
        tLine = word;
      } else {
        tLine = test;
      }
    }
    if (tLine) { ctx.fillText(tLine, textX, textY); textY += titleFontSize * 1.3; }

    // Description
    if (descText) {
      textY += titleFontSize * 0.1;
      ctx.fillStyle = colorScheme.textMuted;
      ctx.font = `400 ${descFontSize}px Inter, system-ui, sans-serif`;
      const descWords = descText.split(' ');
      let dLine = '';
      const maxDescY = itemY + itemSpacing - slideHeight * 0.005;
      for (const word of descWords) {
        const test = dLine ? `${dLine} ${word}` : word;
        if (ctx.measureText(test).width > textMaxWidth && dLine) {
          if (textY + descFontSize * 1.3 > maxDescY) break;
          ctx.fillText(dLine, textX, textY);
          textY += descFontSize * 1.3;
          dLine = word;
        } else {
          dLine = test;
        }
      }
      if (dLine && textY < maxDescY) ctx.fillText(dLine, textX, textY);
    }
  });
}

/**
 * Render big statement (massive centered text that fills the slide)
 */
function renderBigStatement(ctx, text, x, y, maxWidth, maxHeight, style, colorScheme, slideHeight) {
  if (!text) return;

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Auto-size: start large and shrink to fit
  let fontSize = slideHeight * 0.12;
  const minFontSize = slideHeight * 0.05;
  const fontFamily = style?.fontFamily?.split(',')[0] || 'Inter';

  while (fontSize > minFontSize) {
    ctx.font = `800 ${fontSize}px ${fontFamily}`;
    // Check if text fits on one or two lines
    const words = text.split(' ');
    let lines = [];
    let currentLine = '';
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(testLine).width > maxWidth * 0.95 && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) lines.push(currentLine);

    const totalTextHeight = lines.length * fontSize * 1.2;
    if (totalTextHeight <= maxHeight && lines.length <= 3) break;
    fontSize -= 4;
  }

  // Draw the text
  ctx.font = `800 ${fontSize}px ${fontFamily}`;
  ctx.fillStyle = style?.color || colorScheme.text;

  const words = text.split(' ');
  let lines = [];
  let currentLine = '';
  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (ctx.measureText(testLine).width > maxWidth * 0.95 && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) lines.push(currentLine);

  const totalHeight = lines.length * fontSize * 1.2;
  const startY = y + (maxHeight - totalHeight) / 2 + fontSize * 0.6;

  lines.forEach((line, i) => {
    ctx.fillText(line, x + maxWidth / 2, startY + i * fontSize * 1.2);
  });
}

/**
 * Render metrics dashboard (stat boxes in a row)
 */
function renderMetricsDashboard(ctx, items, x, y, maxWidth, maxHeight, style, colorScheme, slideHeight) {
  const count = Math.min(items.length, 4);
  const gap = slideHeight * 0.02;
  const cardWidth = (maxWidth - gap * (count - 1)) / count;
  const cardHeight = maxHeight * 0.8;
  const cardRadius = slideHeight * 0.015;
  const cardY = y + (maxHeight - cardHeight) / 2;

  const cardColors = [
    colorScheme.primary,
    colorScheme.secondary,
    colorScheme.accent,
    colorScheme.primary,
  ];

  items.slice(0, count).forEach((item, i) => {
    const cardX = x + i * (cardWidth + gap);

    // Card background
    ctx.fillStyle = hexToRgba(cardColors[i % cardColors.length], 0.1);
    roundRect(ctx, cardX, cardY, cardWidth, cardHeight, cardRadius);
    ctx.fill();

    // Top accent line
    ctx.fillStyle = cardColors[i % cardColors.length];
    roundRect(ctx, cardX, cardY, cardWidth, slideHeight * 0.005, cardRadius);
    ctx.fill();

    // Parse stat value and label from bullet
    let statValue = '';
    let statLabel = item;
    // Try to extract number from bullet
    const statMatch = item.match(/(\d{1,3}(?:,\d{3})*(?:\.\d+)?[%$MBKxX]?\+?)/);
    if (statMatch) {
      statValue = statMatch[1];
      statLabel = item.replace(statMatch[0], '').replace(/^[\s\-:–—]+|[\s\-:–—]+$/g, '').trim();
    }

    // Stat number (large, centered)
    const statFontSize = slideHeight * 0.06;
    ctx.fillStyle = cardColors[i % cardColors.length];
    ctx.font = `800 ${statFontSize}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(statValue || '—', cardX + cardWidth / 2, cardY + cardHeight * 0.38);

    // Label (smaller, muted, centered)
    const labelFontSize = slideHeight * 0.022;
    ctx.fillStyle = colorScheme.textMuted;
    ctx.font = `500 ${labelFontSize}px Inter, system-ui, sans-serif`;

    // Word wrap label
    const words = statLabel.split(' ');
    let lines = [];
    let currentLine = '';
    const maxLabelWidth = cardWidth * 0.85;
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(testLine).width > maxLabelWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) lines.push(currentLine);

    const labelStartY = cardY + cardHeight * 0.62;
    lines.slice(0, 3).forEach((line, li) => {
      ctx.fillText(line, cardX + cardWidth / 2, labelStartY + li * labelFontSize * 1.4);
    });
  });
}

/**
 * Helper: hex to rgba
 */
function hexToRgba(hex, alpha) {
  if (!hex) return `rgba(128, 128, 128, ${alpha})`;
  if (hex.startsWith('rgba')) return hex;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return `rgba(128, 128, 128, ${alpha})`;
  return `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${alpha})`;
}

/**
 * Extract first statistic from content
 */
function extractFirstStat(content) {
  const text = `${content.title || ''} ${content.content || ''}`;
  const patterns = [
    /(\d{1,3}(?:\.\d+)?%)/,
    /(\$\d{1,3}(?:,\d{3})*(?:\.\d+)?[MBKmk]?)/,
    /(\d+(?:\.\d+)?[xX])/,
    /(\d{3,}(?:,\d{3})*\+?)/,
  ];
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) return match[1];
  }
  return '';
}

// ============================================================================
// ELEMENT RENDERERS
// ============================================================================

/**
 * Render decorative background elements for visually sparse templates
 */
function renderDecorations(ctx, templateId, colorScheme, width, height) {
  switch (templateId) {
    case 'title': {
      // Large subtle circle top-right + smaller bottom-left
      ctx.fillStyle = hexToRgba(colorScheme.primary, 0.06);
      ctx.beginPath();
      ctx.arc(width * 0.85, height * 0.18, width * 0.22, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = hexToRgba(colorScheme.accent || colorScheme.secondary, 0.05);
      ctx.beginPath();
      ctx.arc(width * 0.1, height * 0.85, width * 0.13, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'closing': {
      // Circle bottom-right + top-left
      ctx.fillStyle = hexToRgba(colorScheme.primary, 0.06);
      ctx.beginPath();
      ctx.arc(width * 0.88, height * 0.82, width * 0.18, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = hexToRgba(colorScheme.accent || colorScheme.secondary, 0.05);
      ctx.beginPath();
      ctx.arc(width * 0.12, height * 0.15, width * 0.12, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'section': {
      // Large centered circle behind text
      ctx.fillStyle = hexToRgba(colorScheme.primary, 0.05);
      ctx.beginPath();
      ctx.arc(width * 0.5, height * 0.5, width * 0.25, 0, Math.PI * 2);
      ctx.fill();
      // Subtle right-edge accent
      ctx.fillStyle = hexToRgba(colorScheme.secondary, 0.04);
      ctx.beginPath();
      ctx.arc(width * 1.05, height * 0.5, width * 0.2, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'big-statement': {
      // Subtle diagonal gradient stripe
      ctx.save();
      ctx.globalAlpha = 0.04;
      const grad = ctx.createLinearGradient(0, 0, width, height);
      grad.addColorStop(0, colorScheme.primary);
      grad.addColorStop(0.5, colorScheme.secondary || colorScheme.primary);
      grad.addColorStop(1, 'transparent');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);
      ctx.restore();
      // Corner circles
      ctx.fillStyle = hexToRgba(colorScheme.primary, 0.04);
      ctx.beginPath();
      ctx.arc(width * 0.92, height * 0.12, width * 0.08, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(width * 0.08, height * 0.88, width * 0.06, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    // Content templates: subtle corner accent for visual richness
    case 'bullets':
    case 'cards':
    case 'icon-bullets':
    case 'icon-grid':
    case 'feature-showcase':
    case 'numbered-list':
    case 'three-column':
    case 'process':
    case 'content': {
      ctx.fillStyle = hexToRgba(colorScheme.primary, 0.04);
      ctx.beginPath();
      ctx.arc(width * 0.95, height * 0.06, width * 0.05, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = hexToRgba(colorScheme.secondary || colorScheme.primary, 0.03);
      ctx.beginPath();
      ctx.arc(width * 0.04, height * 0.95, width * 0.04, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    default:
      break;
  }
}

function renderShape(ctx, x, y, width, height, style, colorScheme) {
  // Use gradient for wide accent bars (width > 10x height)
  if (width > height * 10 && colorScheme.secondary) {
    const grad = ctx.createLinearGradient(x, y, x + width, y);
    grad.addColorStop(0, style.backgroundColor || colorScheme.primary);
    grad.addColorStop(1, colorScheme.secondary);
    ctx.fillStyle = grad;
  } else {
    ctx.fillStyle = style.backgroundColor || colorScheme.primary;
  }
  if (style.borderRadius) {
    roundRect(ctx, x, y, width, height, style.borderRadius);
    ctx.fill();
  } else {
    ctx.fillRect(x, y, width, height);
  }
}

function drawTextDecoration(ctx, text, textX, lineY, fontSize, textAlign, decoration) {
  if (!decoration || decoration === 'none') return;
  const textWidth = ctx.measureText(text).width;
  let startX = textX;
  if (textAlign === 'center') startX = textX - textWidth / 2;
  else if (textAlign === 'right') startX = textX - textWidth;
  const lineThickness = Math.max(1, fontSize * 0.06);
  ctx.save();
  ctx.strokeStyle = ctx.fillStyle;
  ctx.lineWidth = lineThickness;
  ctx.beginPath();
  if (decoration === 'underline') {
    const underY = lineY + fontSize * 1.05;
    ctx.moveTo(startX, underY);
    ctx.lineTo(startX + textWidth, underY);
  } else if (decoration === 'line-through') {
    const strikeY = lineY + fontSize * 0.55;
    ctx.moveTo(startX, strikeY);
    ctx.lineTo(startX + textWidth, strikeY);
  }
  ctx.stroke();
  ctx.restore();
}

function renderText(ctx, text, x, y, maxWidth, maxHeight, style, colorScheme, slideHeight) {
  const ws = style._widthScale || 1;
  const fontSize = (style.fontSize / 100) * slideHeight * ws;
  const fontFamily = style.fontFamily || 'Inter, system-ui, sans-serif';
  const fontWeight = style.fontWeight || '400';
  const fontStyle = style.fontStyle === 'italic' ? 'italic ' : '';
  const color = style.color || colorScheme.text;
  const textAlign = style.textAlign || 'left';
  const lineHeight = style.lineHeight || 1.4;
  const textDecoration = style.textDecoration || 'none';

  ctx.fillStyle = color;
  ctx.font = `${fontStyle}${fontWeight} ${fontSize}px ${fontFamily.split(',')[0]}`;
  ctx.textAlign = textAlign;
  ctx.textBaseline = 'top';

  let textX = x;
  if (textAlign === 'center') textX = x + maxWidth / 2;
  else if (textAlign === 'right') textX = x + maxWidth;

  // Word wrap
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) lines.push(currentLine);

  const actualLineHeight = fontSize * lineHeight;
  lines.forEach((line, index) => {
    const lineY = y + index * actualLineHeight;
    if (lineY + fontSize <= y + maxHeight) {
      ctx.fillText(line, textX, lineY);
      drawTextDecoration(ctx, line, textX, lineY, fontSize, textAlign, textDecoration);
    }
  });
}

function renderBullets(ctx, bullets, x, y, maxWidth, maxHeight, style, colorScheme, slideHeight) {
  const ws = style._widthScale || 1;
  const baseFontSize = (style.fontSize / 100) * slideHeight * ws;
  // Scale up font when few bullets to better fill available space
  const scaleFactor = bullets.length <= 3 ? 1.3 : bullets.length <= 5 ? 1.15 : 1.0;
  const fontSize = baseFontSize * scaleFactor;
  const fontFamily = style.fontFamily || 'Inter, system-ui, sans-serif';
  const fontWeight = style.fontWeight || '400';
  const fontStyle = style.fontStyle === 'italic' ? 'italic ' : '';
  const textColor = style.color || colorScheme.textMuted;
  const bulletColor = style.bulletColor || colorScheme.primary;
  const lineHeight = style.lineHeight || 2.2;
  const bulletSize = Math.max(6, fontSize * 0.25);

  ctx.font = `${fontStyle}${fontWeight} ${fontSize}px ${fontFamily.split(',')[0]}`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  const bulletMargin = bulletSize * 3;
  let currentY = y;

  bullets.forEach((bullet) => {
    if (currentY + fontSize > y + maxHeight) return;

    // Draw bullet point
    ctx.fillStyle = bulletColor;
    ctx.beginPath();
    ctx.arc(x + bulletSize, currentY + fontSize / 2, bulletSize, 0, Math.PI * 2);
    ctx.fill();

    // Draw text with word wrap
    ctx.fillStyle = textColor;
    const textX = x + bulletMargin;
    const textMaxWidth = maxWidth - bulletMargin;

    const words = bullet.split(' ');
    let currentLine = '';
    let lineY = currentY;

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);
      if (metrics.width > textMaxWidth && currentLine) {
        ctx.fillText(currentLine, textX, lineY);
        currentLine = word;
        lineY += fontSize * 1.3;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) {
      ctx.fillText(currentLine, textX, lineY);
      lineY += fontSize * 1.3;
    }

    currentY = lineY + fontSize * (lineHeight - 1.3);
  });
}

/**
 * Render a big number/statistic with label
 */
function renderBigNumber(ctx, number, x, y, w, h, style, colorScheme, slideHeight) {
  const numberSize = (style.numberSize || 12) / 100 * slideHeight;
  const labelSize = (style.labelSize || 2) / 100 * slideHeight;

  // Draw the big number
  ctx.fillStyle = style.numberColor || colorScheme.primary;
  ctx.font = `800 ${numberSize}px Inter, system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(number, x + w / 2, y + h * 0.4);

  // Draw label if provided
  if (style.label) {
    ctx.fillStyle = style.labelColor || colorScheme.textMuted;
    ctx.font = `500 ${labelSize}px Inter, system-ui, sans-serif`;
    ctx.fillText(style.label, x + w / 2, y + h * 0.7);
  }
}

/**
 * Render a quote with optional attribution
 */
function renderQuote(ctx, quote, x, y, w, h, style, colorScheme, slideHeight, attribution) {
  const quoteSize = (style.fontSize || 3) / 100 * slideHeight;
  const attrSize = quoteSize * 0.5;

  // Draw quote mark
  ctx.fillStyle = colorScheme.primary;
  ctx.font = `700 ${quoteSize * 3}px Georgia, serif`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.globalAlpha = 0.3;
  ctx.fillText('"', x, y);
  ctx.globalAlpha = 1;

  // Draw quote text
  ctx.fillStyle = style.color || colorScheme.text;
  ctx.font = `400 italic ${quoteSize}px Georgia, serif`;
  ctx.textAlign = 'center';

  // Word wrap quote
  const words = quote.split(' ');
  const lines = [];
  let currentLine = '';
  const maxWidth = w * 0.9;

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (ctx.measureText(testLine).width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) lines.push(currentLine);

  const lineHeight = quoteSize * 1.6;
  const startY = y + h * 0.3;
  lines.forEach((line, i) => {
    ctx.fillText(line, x + w / 2, startY + i * lineHeight);
  });

  // Draw attribution
  if (attribution) {
    ctx.fillStyle = colorScheme.textMuted;
    ctx.font = `500 ${attrSize}px Inter, system-ui, sans-serif`;
    ctx.fillText(`— ${attribution}`, x + w / 2, startY + lines.length * lineHeight + quoteSize);
  }
}

/**
 * Render a comparison (two columns)
 */
function renderComparison(ctx, content, x, y, w, h, style, colorScheme, slideHeight) {
  const colWidth = w * 0.45;
  const gap = w * 0.1;
  const fontSize = (style.fontSize || 2) / 100 * slideHeight;
  const headerSize = fontSize * 1.3;

  // Left column
  ctx.fillStyle = style.leftColor || colorScheme.primary;
  ctx.font = `700 ${headerSize}px Inter, system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText(content.leftHeader || 'Option A', x + colWidth / 2, y);

  // Right column
  ctx.fillStyle = style.rightColor || colorScheme.secondary;
  ctx.fillText(content.rightHeader || 'Option B', x + colWidth + gap + colWidth / 2, y);

  // Draw items
  const itemY = y + headerSize * 2;
  const itemHeight = (h - headerSize * 2) / Math.max(content.leftItems.length, content.rightItems.length);

  ctx.font = `400 ${fontSize}px Inter, system-ui, sans-serif`;
  ctx.textAlign = 'left';

  content.leftItems.forEach((item, i) => {
    ctx.fillStyle = colorScheme.textMuted;
    ctx.fillText(`• ${item}`, x + 20, itemY + i * itemHeight);
  });

  content.rightItems.forEach((item, i) => {
    ctx.fillStyle = colorScheme.textMuted;
    ctx.fillText(`• ${item}`, x + colWidth + gap + 20, itemY + i * itemHeight);
  });
}

/**
 * Render a large stat/number
 */
function renderStat(ctx, value, x, y, w, h, style, colorScheme, slideHeight) {
  const fontSize = (style.fontSize / 100) * slideHeight;
  const fontWeight = style.fontWeight || '800';
  const fontFamily = style.fontFamily || 'Inter, system-ui, sans-serif';
  const color = style.color || colorScheme.primary;

  ctx.fillStyle = color;
  ctx.font = `${fontWeight} ${fontSize}px ${fontFamily.split(',')[0]}`;
  ctx.textAlign = style.textAlign || 'center';
  ctx.textBaseline = 'middle';

  ctx.fillText(value, x + w / 2, y + h / 2);
}

/**
 * Render a timeline with years and events
 */
function renderTimeline(ctx, items, x, y, w, h, style, colorScheme, slideHeight) {
  if (!items || items.length === 0) return;

  const fontSize = Math.max(((style.fontSize || 2.2) / 100) * slideHeight, slideHeight * 0.026);
  const yearSize = Math.max(((style.yearSize || 2.8) / 100) * slideHeight, slideHeight * 0.032);
  const color = style.color || colorScheme.textMuted;
  const yearColor = style.yearColor || colorScheme.primary;
  const lineColor = style.lineColor || colorScheme.primary;
  const dotColor = style.dotColor || colorScheme.primary;

  const itemWidth = w / items.length;
  // Timeline line at 15% of available height — push up to give more room for text below
  const lineY = y + h * 0.15;
  const dotRadius = slideHeight * 0.012;
  const lineInset = slideHeight * 0.02;

  // Subtle background band
  ctx.fillStyle = hexToRgba(lineColor, 0.04);
  roundRect(ctx, x, lineY - slideHeight * 0.04, w, slideHeight * 0.08, slideHeight * 0.008);
  ctx.fill();

  // Timeline line
  ctx.strokeStyle = lineColor;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(x + lineInset, lineY);
  ctx.lineTo(x + w - lineInset, lineY);
  ctx.stroke();

  items.forEach((item, i) => {
    const itemX = x + itemWidth * i + itemWidth / 2;

    // Ring-style dot
    ctx.fillStyle = dotColor;
    ctx.beginPath();
    ctx.arc(itemX, lineY, dotRadius, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = colorScheme.background || '#ffffff';
    ctx.beginPath();
    ctx.arc(itemX, lineY, dotRadius * 0.5, 0, Math.PI * 2);
    ctx.fill();

    // Year above line
    ctx.font = `700 ${yearSize}px Inter, system-ui, sans-serif`;
    ctx.fillStyle = yearColor;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText(item.year || '', itemX, lineY - dotRadius - slideHeight * 0.01);

    // Text card below the line — fills remaining height
    const cardTop = lineY + dotRadius + slideHeight * 0.015;
    const cardBottom = y + h;
    const cardWidth = itemWidth - slideHeight * 0.015;
    const cardPadding = slideHeight * 0.01;

    // Subtle card background per item
    ctx.fillStyle = hexToRgba(dotColor, 0.04);
    roundRect(ctx, itemX - cardWidth / 2, cardTop, cardWidth, cardBottom - cardTop, slideHeight * 0.008);
    ctx.fill();

    ctx.font = `500 ${fontSize}px Inter, system-ui, sans-serif`;
    ctx.fillStyle = color;
    ctx.textBaseline = 'top';

    const maxTextWidth = cardWidth - cardPadding * 2;
    const words = (item.text || '').split(' ');
    let lines = [];
    let currentLine = '';
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(testLine).width > maxTextWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) lines.push(currentLine);

    const cardInnerH = cardBottom - cardTop - cardPadding * 2;
    const maxLines = Math.floor(cardInnerH / (fontSize * 1.4));
    const visibleLines = lines.slice(0, maxLines);
    const textBlockH = visibleLines.length * fontSize * 1.4;
    const textTopOffset = Math.max(0, (cardInnerH - textBlockH) / 2);
    let textY = cardTop + cardPadding + textTopOffset;
    visibleLines.forEach(line => {
      ctx.fillText(line, itemX, textY);
      textY += fontSize * 1.4;
    });
  });
}

/**
 * Render a process flow with numbered steps and arrows
 */
function renderProcess(ctx, items, x, y, w, h, style, colorScheme, slideHeight) {
  if (!items || items.length === 0) return;

  const fontSize = Math.max((style.fontSize / 100) * slideHeight, slideHeight * 0.024);
  const stepSize = ((style.stepNumberSize || 3) / 100) * slideHeight;
  const color = style.color || colorScheme.textMuted;
  const stepBgColor = style.stepNumberBg || colorScheme.primary;
  const stepTextColor = style.stepNumberColor || colorScheme.text;
  const arrowColor = style.arrowColor || colorScheme.primary;

  const gap = slideHeight * 0.015;
  const itemWidth = (w - gap * (items.length - 1)) / items.length;
  const circleRadius = stepSize * 0.7;

  // Step number row at top 15% of area
  const circleY = y + h * 0.10;
  // Connecting line between circles
  const lineY = circleY;

  items.forEach((item, i) => {
    const itemX = x + i * (itemWidth + gap);
    const centerX = itemX + itemWidth / 2;

    // Draw step circle
    ctx.fillStyle = stepBgColor;
    ctx.beginPath();
    ctx.arc(centerX, circleY, circleRadius, 0, Math.PI * 2);
    ctx.fill();

    // Step number
    ctx.font = `700 ${stepSize * 0.7}px Inter, system-ui, sans-serif`;
    ctx.fillStyle = stepTextColor;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText((i + 1).toString(), centerX, circleY);

    // Arrow to next step
    if (i < items.length - 1) {
      const arrowStartX = centerX + circleRadius + gap * 0.3;
      const arrowEndX = centerX + itemWidth + gap - circleRadius - gap * 0.3;

      ctx.strokeStyle = arrowColor;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(arrowStartX, lineY);
      ctx.lineTo(arrowEndX - 8, lineY);
      ctx.stroke();

      ctx.fillStyle = arrowColor;
      ctx.beginPath();
      ctx.moveTo(arrowEndX, lineY);
      ctx.lineTo(arrowEndX - 10, lineY - 5);
      ctx.lineTo(arrowEndX - 10, lineY + 5);
      ctx.closePath();
      ctx.fill();
    }

    // Text card below circle — fills remaining height
    const cardTop = circleY + circleRadius + slideHeight * 0.02;
    const cardBottom = y + h;
    const cardHeight = cardBottom - cardTop;
    const cardPadding = slideHeight * 0.012;

    // Subtle card background
    ctx.fillStyle = hexToRgba(stepBgColor, 0.05);
    roundRect(ctx, itemX, cardTop, itemWidth, cardHeight, slideHeight * 0.008);
    ctx.fill();

    // Split into title:description
    const rawText = item.replace(/^\d+[.)\-]\s*/, '').trim();
    let titleText = rawText;
    let descText = '';
    const colonIdx = rawText.indexOf(':');
    const dotIdx = rawText.indexOf('. ');
    if (colonIdx > 0 && colonIdx < 60) {
      titleText = rawText.substring(0, colonIdx).trim();
      descText = rawText.substring(colonIdx + 1).trim();
    } else if (dotIdx > 0 && dotIdx < 60) {
      titleText = rawText.substring(0, dotIdx).trim();
      descText = rawText.substring(dotIdx + 2).trim();
    }

    const titleFontSize = fontSize * 1.2;
    const maxTextWidth = itemWidth - cardPadding * 2;

    // Pre-measure title lines
    ctx.font = `600 ${titleFontSize}px Inter, system-ui, sans-serif`;
    const titleWords = titleText.split(' ');
    let tLines = [];
    let tLine = '';
    for (const word of titleWords) {
      const test = tLine ? `${tLine} ${word}` : word;
      if (ctx.measureText(test).width > maxTextWidth && tLine) { tLines.push(tLine); tLine = word; }
      else tLine = test;
    }
    if (tLine) tLines.push(tLine);
    tLines = tLines.slice(0, 2);

    // Pre-measure description lines
    let dLines = [];
    if (descText) {
      ctx.font = `400 ${fontSize}px Inter, system-ui, sans-serif`;
      let dLine = '';
      for (const word of descText.split(' ')) {
        const test = dLine ? `${dLine} ${word}` : word;
        if (ctx.measureText(test).width > maxTextWidth && dLine) { dLines.push(dLine); dLine = word; }
        else dLine = test;
      }
      if (dLine) dLines.push(dLine);
    }

    // Calculate content height and vertically center in card
    const titleH = tLines.length * titleFontSize * 1.3;
    const descH = dLines.length > 0 ? fontSize * 0.2 + dLines.length * fontSize * 1.4 : 0;
    const totalTextH = titleH + descH;
    const cardInnerH = cardHeight - cardPadding * 2;
    const textTopOffset = Math.max(0, (cardInnerH - totalTextH) / 2);

    let textY = cardTop + cardPadding + textTopOffset;

    // Title (bold)
    ctx.fillStyle = colorScheme.text;
    ctx.font = `600 ${titleFontSize}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    tLines.forEach(line => {
      ctx.fillText(line, itemX + itemWidth / 2, textY);
      textY += titleFontSize * 1.3;
    });

    // Description
    if (dLines.length > 0) {
      textY += fontSize * 0.2;
      ctx.fillStyle = color;
      ctx.font = `400 ${fontSize}px Inter, system-ui, sans-serif`;
      const maxDescY = cardBottom - cardPadding;
      dLines.forEach(line => {
        if (textY + fontSize > maxDescY) return;
        ctx.fillText(line, itemX + itemWidth / 2, textY);
        textY += fontSize * 1.4;
      });
    }
  });
}

async function renderImage(ctx, imageSrc, x, y, width, height, style) {
  try {
    const img = await loadImage(imageSrc);
    if (style.borderRadius) {
      ctx.save();
      roundRect(ctx, x, y, width, height, style.borderRadius);
      ctx.clip();
      ctx.drawImage(img, x, y, width, height);
      ctx.restore();
    } else {
      ctx.drawImage(img, x, y, width, height);
    }
  } catch (e) {
    console.warn('Failed to load image:', e);
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function parseGradient(ctx, gradientStr, width, height) {
  const angleMatch = gradientStr.match(/(\d+)deg/);
  const angle = angleMatch ? parseInt(angleMatch[1]) : 135;
  const radians = (angle - 90) * (Math.PI / 180);
  const x1 = width / 2 - Math.cos(radians) * width;
  const y1 = height / 2 - Math.sin(radians) * height;
  const x2 = width / 2 + Math.cos(radians) * width;
  const y2 = height / 2 + Math.sin(radians) * height;

  const gradient = ctx.createLinearGradient(x1, y1, x2, y2);
  const colorRegex = /#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3}|rgba?\([^)]+\)/g;
  const colors = gradientStr.match(colorRegex) || ['#1a1a2e', '#16213e'];
  colors.forEach((color, i) => {
    gradient.addColorStop(i / Math.max(colors.length - 1, 1), color);
  });
  return gradient;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function roundRect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// ============================================================================
// EDITABLE LAYERS GENERATION
// ============================================================================

/**
 * Generate editable layers from template elements for direct editing
 * This allows template-generated slides to be immediately editable without
 * needing to call the backend extraction API
 */
function generateEditableLayers(template, content, colorScheme, width = DEFAULT_WIDTH, height = DEFAULT_HEIGHT) {
  const layers = [];
  let layerId = 0;

  for (const element of template.layout.elements) {
    const x = Math.round((element.position.x / 100) * width);
    const y = Math.round((element.position.y / 100) * height);
    const w = Math.round((element.position.width / 100) * width);
    const h = Math.round((element.position.height / 100) * height);

    // Determine content based on element type
    let layerContent = '';
    let layerType = 'shape';

    switch (element.type) {
      case 'title':
      case 'subtitle':
      case 'content':
        layerContent = content[element.placeholder] || '';
        layerType = 'text';
        break;
      case 'stat':
        layerContent = content.stat || content[element.placeholder] || '';
        layerType = 'text';
        break;
      case 'bullets':
      case 'icon-bullets':
        const bullets = content.bulletPoints || content.bullet_points || [];
        layerContent = bullets.join('\n• ');
        if (layerContent) layerContent = '• ' + layerContent;
        layerType = 'text';
        break;
      case 'big-statement':
        layerContent = content[element.placeholder] || content.title || '';
        layerType = 'text';
        break;
      case 'numbered-list':
      case 'feature-showcase':
      case 'metrics-dashboard':
      case 'cards':
      case 'icon-grid':
      case 'three-column':
        const listBullets = content.bulletPoints || content.bullet_points || [];
        layerContent = listBullets.join('\n• ');
        if (layerContent) layerContent = '• ' + layerContent;
        layerType = 'text';
        break;
      case 'tag-label':
        layerContent = getTagLabelText(content);
        layerType = 'text';
        break;
      case 'image-placeholder':
        layerType = 'image';
        layerContent = content._slideImageUrl || '';
        break;
      case 'shape':
        layerType = 'shape';
        break;
      default:
        // Handle icon-grid, cards, etc. as composite elements
        layerType = element.type;
        layerContent = JSON.stringify(content.bulletPoints || content.bullet_points || []);
    }

    // Skip empty text layers
    if (layerType === 'text' && !layerContent) continue;

    // Calculate font size in pixels
    const fontSize = element.style?.fontSize
      ? Math.round((element.style.fontSize / 100) * height)
      : 24;

    layers.push({
      id: `layer_${layerId++}`,
      type: layerType,
      content: layerContent,
      bbox: [x, y, x + w, y + h],
      x: x,
      y: y,
      width: w,
      height: h,
      visible: true,
      locked: false,
      zIndex: layerId,
      textStyle: {
        fontSize: fontSize,
        fontFamily: element.style?.fontFamily || 'Inter, system-ui, sans-serif',
        fontWeight: element.style?.fontWeight || '400',
        fontStyle: element.style?.fontStyle || 'normal',
        textDecoration: element.style?.textDecoration || 'none',
        color: element.style?.color || colorScheme.text,
        textAlign: element.style?.textAlign || 'left',
      },
      elementType: element.type,
      elementId: element.id,
    });
  }

  return layers;
}

// ============================================================================
// MAIN EXPORT - Generate All Slides
// ============================================================================

/**
 * Render image placeholder — gradient block with topic icon
 */
/**
 * Render an actual image (from URL or data URL) with cover-fit and border-radius clipping
 */
async function renderActualImage(ctx, imageUrl, x, y, w, h, style) {
  const radius = style.borderRadius || 16;

  try {
    const img = await loadImage(imageUrl);
    ctx.save();
    roundRect(ctx, x, y, w, h, radius);
    ctx.clip();
    drawCoverImage(ctx, img, x, y, w, h);
    ctx.restore();
  } catch (e) {
    // Fallback: draw a subtle placeholder if image fails to load
    ctx.save();
    roundRect(ctx, x, y, w, h, radius);
    ctx.clip();
    ctx.fillStyle = 'rgba(100,100,120,0.15)';
    ctx.fillRect(x, y, w, h);
    ctx.restore();
  }
}

/** Draw image with cover-fit (fills area, crops overflow) */
function drawCoverImage(ctx, img, x, y, w, h) {
  const imgRatio = img.naturalWidth / img.naturalHeight;
  const boxRatio = w / h;
  let sx, sy, sw, sh;
  if (imgRatio > boxRatio) {
    // Image wider than box — crop sides
    sh = img.naturalHeight;
    sw = sh * boxRatio;
    sx = (img.naturalWidth - sw) / 2;
    sy = 0;
  } else {
    // Image taller than box — crop top/bottom
    sw = img.naturalWidth;
    sh = sw / boxRatio;
    sx = 0;
    sy = (img.naturalHeight - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

function renderImagePlaceholder(ctx, titleText, x, y, w, h, style, colorScheme, slideHeight) {
  const radius = style.borderRadius || 16;
  const primary = style.primaryColor || colorScheme.primary;
  const secondary = style.secondaryColor || colorScheme.secondary || primary;

  // Gradient fill
  const grad = ctx.createLinearGradient(x, y, x + w, y + h);
  grad.addColorStop(0, primary);
  grad.addColorStop(1, secondary);

  ctx.shadowColor = 'rgba(0,0,0,0.1)';
  ctx.shadowBlur = 20;
  ctx.shadowOffsetY = 6;
  ctx.fillStyle = grad;
  roundRect(ctx, x, y, w, h, radius);
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  // Centered icon based on title content
  const iconName = selectIconForContent(titleText) || 'image';
  const iconSize = Math.min(w, h) * 0.25;
  ctx.globalAlpha = 0.3;
  renderIcon(ctx, iconName, x + (w - iconSize) / 2, y + (h - iconSize) / 2, iconSize, '#ffffff');
  ctx.globalAlpha = 1.0;

  // Subtle decorative circles
  ctx.fillStyle = 'rgba(255,255,255,0.08)';
  ctx.beginPath();
  ctx.arc(x + w * 0.75, y + h * 0.25, w * 0.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(x + w * 0.2, y + h * 0.8, w * 0.12, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * Render a skeleton loading placeholder while an image is being generated
 */
function renderSkeletonPlaceholder(ctx, x, y, w, h, style, colorScheme, slideHeight) {
  const radius = style.borderRadius || 16;

  // Light gray background
  ctx.save();
  roundRect(ctx, x, y, w, h, radius);
  ctx.clip();

  // Base fill — light gray
  ctx.fillStyle = 'rgba(180, 185, 195, 0.15)';
  ctx.fillRect(x, y, w, h);

  // Shimmer gradient sweep (diagonal, lighter band to suggest loading)
  const shimmerGrad = ctx.createLinearGradient(x, y, x + w, y + h);
  shimmerGrad.addColorStop(0, 'rgba(180, 185, 195, 0.08)');
  shimmerGrad.addColorStop(0.35, 'rgba(220, 225, 235, 0.25)');
  shimmerGrad.addColorStop(0.5, 'rgba(240, 242, 248, 0.35)');
  shimmerGrad.addColorStop(0.65, 'rgba(220, 225, 235, 0.25)');
  shimmerGrad.addColorStop(1, 'rgba(180, 185, 195, 0.08)');
  ctx.fillStyle = shimmerGrad;
  ctx.fillRect(x, y, w, h);

  // Fake skeleton shapes — mock image composition lines
  const barH = slideHeight * 0.008;
  const barY1 = y + h * 0.35;
  const barY2 = y + h * 0.45;
  const barY3 = y + h * 0.55;
  ctx.fillStyle = 'rgba(160, 165, 175, 0.12)';
  roundRect(ctx, x + w * 0.15, barY1, w * 0.7, barH, 4);
  ctx.fill();
  roundRect(ctx, x + w * 0.2, barY2, w * 0.6, barH, 4);
  ctx.fill();
  roundRect(ctx, x + w * 0.25, barY3, w * 0.5, barH, 4);
  ctx.fill();

  // Small image icon in center
  const iconSize = Math.min(w, h) * 0.15;
  ctx.globalAlpha = 0.2;
  renderIcon(ctx, 'image', x + (w - iconSize) / 2, y + h * 0.18, iconSize, colorScheme.textMuted || '#888');
  ctx.globalAlpha = 1.0;

  // "Generating image..." text at bottom
  const textSize = slideHeight * 0.016;
  ctx.font = `400 ${textSize}px Inter, system-ui, sans-serif`;
  ctx.fillStyle = colorScheme.textMuted || 'rgba(120, 125, 135, 0.6)';
  ctx.textAlign = 'center';
  ctx.fillText('Generating image...', x + w / 2, y + h * 0.78);

  ctx.restore();
}

/**
 * Render three-column layout with colored blocks
 */
function renderThreeColumns(ctx, bullets, x, y, maxWidth, maxHeight, style, colorScheme, slideHeight) {
  const cols = 3;
  const items = bullets.slice(0, 6);
  const gap = slideHeight * 0.018;
  const colWidth = (maxWidth - gap * (cols - 1)) / cols;
  const colHeight = maxHeight;
  const baseFontSize = (style.fontSize / 100) * slideHeight;
  const fontSize = Math.max(baseFontSize, slideHeight * 0.024);
  const titleSize = Math.max(fontSize * 1.15, slideHeight * 0.032);
  const colRadius = slideHeight * 0.012;
  const colPadding = slideHeight * 0.022;
  const accentBarH = slideHeight * 0.005;

  const icons = selectIconsForBullets(items);
  const accentColors = [colorScheme.primary, colorScheme.secondary, colorScheme.accent || colorScheme.primary];

  for (let c = 0; c < cols; c++) {
    if (c >= items.length) break;

    const colX = x + c * (colWidth + gap);
    const accentColor = accentColors[c % 3];

    // Column card background
    ctx.shadowColor = 'rgba(0,0,0,0.06)';
    ctx.shadowBlur = 10;
    ctx.shadowOffsetY = 3;
    ctx.fillStyle = hexToRgba(accentColor, 0.06);
    roundRect(ctx, colX, y, colWidth, colHeight, colRadius);
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    // Top accent bar
    ctx.fillStyle = accentColor;
    roundRect(ctx, colX, y, colWidth, accentBarH, colRadius);
    ctx.fill();

    // Collect items for this column
    const colItems = [items[c]];
    if (items[c + cols] !== undefined) colItems.push(items[c + cols]);

    const iconSize = slideHeight * 0.045;
    const textMaxW = colWidth - colPadding * 2;

    // For single-item columns, vertically center content
    if (colItems.length === 1) {
      const item = colItems[0];
      let titleText = item;
      let descText = '';
      const colonIdx = item.indexOf(':');
      const dotIdx = item.indexOf('. ');
      if (colonIdx > 0 && colonIdx < 60) { titleText = item.substring(0, colonIdx).trim(); descText = item.substring(colonIdx + 1).trim(); }
      else if (dotIdx > 0 && dotIdx < 60) { titleText = item.substring(0, dotIdx).trim(); descText = item.substring(dotIdx + 2).trim(); }

      // Pre-measure
      ctx.font = `700 ${titleSize}px Inter, system-ui, sans-serif`;
      let titleLines = [];
      let tl = '';
      for (const word of titleText.split(' ')) {
        const test = tl ? `${tl} ${word}` : word;
        if (ctx.measureText(test).width > textMaxW && tl) { titleLines.push(tl); tl = word; }
        else tl = test;
      }
      if (tl) titleLines.push(tl);

      let descLines = [];
      if (descText) {
        ctx.font = `400 ${fontSize}px Inter, system-ui, sans-serif`;
        let dl = '';
        for (const word of descText.split(' ')) {
          const test = dl ? `${dl} ${word}` : word;
          if (ctx.measureText(test).width > textMaxW && dl) { descLines.push(dl); dl = word; }
          else dl = test;
        }
        if (dl) descLines.push(dl);
      }

      const contentH = iconSize + slideHeight * 0.012 + titleLines.length * titleSize * 1.3
        + (descLines.length > 0 ? fontSize * 0.2 + descLines.length * fontSize * 1.4 : 0);
      const availInner = colHeight - accentBarH - colPadding * 2;
      const topOffset = Math.max(0, (availInner - contentH) / 2);

      let curY = y + accentBarH + colPadding + topOffset;
      renderIcon(ctx, icons[c] || 'star', colX + colPadding, curY, iconSize, accentColor, {
        backgroundColor: hexToRgba(accentColor, 0.15), backgroundRadius: iconSize * 0.6,
      });
      curY += iconSize + slideHeight * 0.012;

      ctx.fillStyle = colorScheme.text;
      ctx.font = `700 ${titleSize}px Inter, system-ui, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      titleLines.forEach(line => { ctx.fillText(line, colX + colPadding, curY); curY += titleSize * 1.3; });

      if (descLines.length > 0) {
        curY += fontSize * 0.2;
        ctx.fillStyle = colorScheme.textMuted;
        ctx.font = `400 ${fontSize}px Inter, system-ui, sans-serif`;
        const maxY = y + colHeight - colPadding;
        descLines.forEach(line => { if (curY + fontSize > maxY) return; ctx.fillText(line, colX + colPadding, curY); curY += fontSize * 1.4; });
      }
    } else {
      // 2 items: split column in half
      colItems.forEach((item, itemIdx) => {
        const halfH = (colHeight - accentBarH - colPadding * 2) / 2;
        const startY = y + accentBarH + colPadding + itemIdx * (halfH + slideHeight * 0.01);

        renderIcon(ctx, icons[c + itemIdx * cols] || 'star', colX + colPadding, startY, iconSize, accentColor, {
          backgroundColor: hexToRgba(accentColor, 0.15), backgroundRadius: iconSize * 0.6,
        });

        let titleText = item;
        let descText = '';
        const colonIdx = item.indexOf(':');
        const dotIdx = item.indexOf('. ');
        if (colonIdx > 0 && colonIdx < 60) { titleText = item.substring(0, colonIdx).trim(); descText = item.substring(colonIdx + 1).trim(); }
        else if (dotIdx > 0 && dotIdx < 60) { titleText = item.substring(0, dotIdx).trim(); descText = item.substring(dotIdx + 2).trim(); }

        let textY = startY + iconSize + slideHeight * 0.012;
        ctx.fillStyle = colorScheme.text;
        ctx.font = `700 ${titleSize}px Inter, system-ui, sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        let tLine = '';
        for (const word of titleText.split(' ')) {
          const test = tLine ? `${tLine} ${word}` : word;
          if (ctx.measureText(test).width > textMaxW && tLine) { ctx.fillText(tLine, colX + colPadding, textY); textY += titleSize * 1.3; tLine = word; }
          else tLine = test;
        }
        if (tLine) { ctx.fillText(tLine, colX + colPadding, textY); textY += titleSize * 1.4; }

        if (descText) {
          ctx.fillStyle = colorScheme.textMuted;
          ctx.font = `400 ${fontSize}px Inter, system-ui, sans-serif`;
          let dLine = '';
          const maxDescY = startY + halfH - slideHeight * 0.01;
          for (const word of descText.split(' ')) {
            const test = dLine ? `${dLine} ${word}` : word;
            if (ctx.measureText(test).width > textMaxW && dLine) {
              if (textY + fontSize * 1.4 > maxDescY) break;
              ctx.fillText(dLine, colX + colPadding, textY); textY += fontSize * 1.4; dLine = word;
            } else dLine = test;
          }
          if (dLine && textY < maxDescY) ctx.fillText(dLine, colX + colPadding, textY);
        }
      });
    }
  }
}

/**
 * Render quote card with decorative frame and large quotation marks
 */
function renderQuoteCard(ctx, quoteText, attribution, x, y, w, h, style, colorScheme, slideHeight) {
  const fontSize = ((style.fontSize || 3.5) / 100) * slideHeight;
  const borderColor = style.borderColor || colorScheme.primary;
  const quoteMarkColor = style.quoteMarkColor || colorScheme.primary;
  const padding = slideHeight * 0.06;

  // Decorative border frame
  ctx.strokeStyle = hexToRgba(borderColor, 0.2);
  ctx.lineWidth = 2;
  roundRect(ctx, x + padding * 0.5, y + padding * 0.5, w - padding, h - padding, slideHeight * 0.02);
  ctx.stroke();

  // Large opening quotation mark
  ctx.font = `700 ${slideHeight * 0.15}px Georgia, serif`;
  ctx.fillStyle = hexToRgba(quoteMarkColor, 0.15);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText('\u201C', x + padding, y + padding * 0.8);

  // Large closing quotation mark
  ctx.textAlign = 'right';
  ctx.fillText('\u201D', x + w - padding, y + h - padding * 2.5);

  // Quote text centered
  ctx.fillStyle = style.color || colorScheme.text;
  ctx.font = `500 ${fontSize}px Inter, system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';

  const maxTextWidth = w - padding * 4;
  const words = quoteText.split(' ');
  let lines = [];
  let currentLine = '';
  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (ctx.measureText(testLine).width > maxTextWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) lines.push(currentLine);

  const totalHeight = lines.length * fontSize * 1.5;
  const startY = y + (h - totalHeight) / 2 - padding * 0.5;

  lines.forEach((line, i) => {
    ctx.fillText(line, x + w / 2, startY + i * fontSize * 1.5);
  });

  // Attribution
  if (attribution) {
    ctx.fillStyle = colorScheme.textMuted;
    ctx.font = `400 ${fontSize * 0.7}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(attribution, x + w / 2, startY + totalHeight + fontSize);
  }
}

/**
 * Generate all slides for a presentation using smart templates
 * Now includes extractedLayers for immediate editability
 */
export async function generateSlidesWithTemplates(theme, slides, brandGuidelines, onProgress, onSlideGenerated, imageGenerator) {
  const colorScheme = applyBrandColors(theme.colorScheme, brandGuidelines);
  const generatedSlides = [];

  // Helper to yield to browser for UI updates
  const yieldToBrowser = () => new Promise(resolve => setTimeout(resolve, 0));

  for (let i = 0; i < slides.length; i++) {
    // Yield before each slide to allow React to process previous state update
    if (onSlideGenerated && i > 0) {
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    const slide = slides[i];
    // Only allow image variants when imageGenerator is available (user opted in)
    const slideForTemplate = (!imageGenerator && (slide.needsImage || slide.needs_image))
      ? { ...slide, needsImage: false, needs_image: false }
      : slide;
    const template = getTemplateForSlide(theme, slideForTemplate, i, slides.length);

    const content = {
      title: slide.title || '',
      content: slide.content || '',
      bullet_points: slide.bulletPoints || slide.bullet_points || [],
      bulletPoints: slide.bulletPoints || slide.bullet_points || [],
      image: slide.image || null,
      number: slide.number || null,
      stat: slide.stat || null,
      quote: slide.quote || null,
      attribution: slide.attribution || null,
      leftItems: slide.leftItems || null,
      rightItems: slide.rightItems || null,
      leftHeader: slide.leftHeader || null,
      rightHeader: slide.rightHeader || null,
      slideType: slide.slideType || slide.slide_type || template.id || '',
      _needsImageLoading: (slide.needsImage || slide.needs_image) && !!imageGenerator,
    };

    // Render background-only first (for streaming animation)
    const backgroundOnlyUrl = await renderTemplateBackgroundOnly(template, content, colorScheme);

    // Yield to let React show the background while we render the full slide
    await yieldToBrowser();

    const previewUrl = await renderTemplateToImage(template, content, colorScheme);

    // Yield after canvas rendering to let UI breathe
    await yieldToBrowser();

    // Generate editable layers from template (no backend extraction needed)
    const editableLayers = generateEditableLayers(template, content, colorScheme);

    // Check if this slide needs an AI-generated image
    const needsImage = slide.needsImage || slide.needs_image;
    const suggestedImage = slide.suggestedImage || slide.suggested_image || '';

    const generatedSlide = {
      index: i,
      title: content.title,
      content: content.content,
      bullet_points: content.bullet_points,
      slideType: slide.slideType || slide.slide_type || null,
      preview_url: previewUrl,
      backgroundOnlyUrl: backgroundOnlyUrl,
      template_id: template.id,
      layout: template.layout,
      description: `Generated using ${theme.name} theme - ${template.name} template`,
      // Flag for async image generation
      _needsImage: needsImage && !!suggestedImage && !!imageGenerator,
      _suggestedImage: suggestedImage,
      // Include pre-generated editable layers so slides are immediately editable
      extractedLayers: {
        background: previewUrl,
        background_image: previewUrl, // Backwards compatibility
        backgroundOnly: backgroundOnlyUrl,
        layers: editableLayers,
        source: 'template',
        canvas: { width: 1920, height: 1080 },
      },
    };

    generatedSlides.push(generatedSlide);

    // Stream the slide immediately if callback provided (Gamma-style)
    if (onSlideGenerated) {
      onSlideGenerated(generatedSlide, i, slides.length);
      // Force a yield after callback to ensure React renders
      await yieldToBrowser();
    }

    if (onProgress) {
      onProgress(i + 1, slides.length);
    }
  }

  // After all slides rendered, generate images in the background for slides that need them
  // This doesn't block the initial rendering — slides appear instantly
  if (imageGenerator) {
    const imageSlides = generatedSlides.filter(s => s._needsImage);
    if (imageSlides.length > 0) {
      // Fire all image generations in parallel, don't await them here
      // The caller should handle re-rendering via the onImageReady callback
      const imagePromises = imageSlides.map(async (slide) => {
        try {
          const imageUrl = await imageGenerator(slide._suggestedImage, slide.title || '');
          if (imageUrl) {
            return { index: slide.index, imageUrl };
          }
        } catch (err) {
          console.warn(`Failed to generate image for slide ${slide.index + 1}:`, err);
        }
        return null;
      });

      // Return both the slides and a promise for the images
      generatedSlides._imagePromises = Promise.all(imagePromises).then(results =>
        results.filter(Boolean)
      );
    }
  }

  return generatedSlides;
}

export { generateEditableLayers };

export default {
  renderTemplateToImage,
  renderTemplateBackgroundOnly,
  generateSlidesWithTemplates,
  generateEditableLayers,
  analyzeContent,
};
